import { edgeFunctions } from '../lib/supabase'
import {
  isShowDirectoryPickerWriteAvailable,
  pickBackupDirectory,
} from './storageBackup'
import type {
  EdgeFunctionFile,
  EdgeFunctionFilesPayload,
  EdgeFunctionMeta,
  EdgeFunctionsBackupProgress,
} from '../types/edgeFunctionsBackup'

export { isShowDirectoryPickerWriteAvailable, pickBackupDirectory }

type FileSystemWritableFileStreamLike = {
  write: (data: BufferSource | Blob | string) => Promise<void>
  close: () => Promise<void>
}

type FileSystemFileHandleLike = {
  createWritable: () => Promise<FileSystemWritableFileStreamLike>
}

type FileSystemDirectoryHandleLike = {
  kind: 'directory'
  name: string
  getDirectoryHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FileSystemDirectoryHandleLike>
  getFileHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FileSystemFileHandleLike>
}

function parseInvokeError(error: unknown, data: unknown): string {
  const payload = (data ?? {}) as { error?: string; message?: string }
  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error
  }
  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message
  }
  const errMsg = (error as { message?: string } | null)?.message || ''
  const status =
    (error as { status?: number; context?: { status?: number } } | null)?.status ??
    (error as { context?: { status?: number } } | null)?.context?.status
  if (status === 404 || errMsg.toLowerCase().includes('not found')) {
    return 'Função edge-functions-backup não encontrada. Publique a edge function no Supabase.'
  }
  if (status === 401 || errMsg.toLowerCase().includes('jwt')) {
    return 'Sessão expirada. Faça login novamente.'
  }
  return errMsg || 'Erro ao comunicar com edge-functions-backup'
}

function base64ToUint8Array(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i)
  }
  return out
}

async function ensureNestedDirectory(
  root: FileSystemDirectoryHandleLike,
  parts: string[],
): Promise<FileSystemDirectoryHandleLike> {
  let current = root
  for (const part of parts) {
    if (!part || part === '.' || part === '..') continue
    current = await current.getDirectoryHandle(part, { create: true })
  }
  return current
}

async function writeTextOrBinaryFile(
  root: FileSystemDirectoryHandleLike,
  relativePath: string,
  file: EdgeFunctionFile,
): Promise<void> {
  const segments = relativePath.split('/').filter(Boolean)
  if (segments.length === 0) throw new Error('Caminho inválido')
  const fileName = segments[segments.length - 1]
  const dir = await ensureNestedDirectory(root, segments.slice(0, -1))
  const handle = await dir.getFileHandle(fileName, { create: true })
  const writable = await handle.createWritable()
  try {
    if (file.encoding === 'base64') {
      await writable.write(base64ToUint8Array(file.content))
    } else {
      await writable.write(file.content)
    }
  } finally {
    await writable.close()
  }
}

export const edgeFunctionsBackupService = {
  async list(): Promise<EdgeFunctionMeta[]> {
    const { data, error } = await edgeFunctions.invoke('edge-functions-backup', {
      action: 'list',
    })

    if (error) {
      throw new Error(parseInvokeError(error, data))
    }

    const payload = (data ?? {}) as {
      functions?: EdgeFunctionMeta[]
      error?: string
    }
    if (payload.error) throw new Error(payload.error)
    return Array.isArray(payload.functions) ? payload.functions : []
  },

  async getFiles(slug: string): Promise<EdgeFunctionFilesPayload> {
    const { data, error } = await edgeFunctions.invoke('edge-functions-backup', {
      action: 'get_files',
      slug,
    })

    if (error) {
      throw new Error(parseInvokeError(error, data))
    }

    const payload = (data ?? {}) as EdgeFunctionFilesPayload & { error?: string }
    if (payload.error) throw new Error(payload.error)
    if (!payload.slug || !Array.isArray(payload.files)) {
      throw new Error('Resposta inválida ao baixar a Edge Function')
    }
    return {
      slug: payload.slug,
      files: payload.files,
      count: Number(payload.count) || payload.files.length,
    }
  },
}

export type RunEdgeFunctionsBackupOptions = {
  slugs: string[]
  rootDir: FileSystemDirectoryHandleLike
  /** Pasta raiz dentro do diretório escolhido (default: edge-functions) */
  baseFolder?: string
  signal?: AbortSignal
  onProgress?: (progress: EdgeFunctionsBackupProgress) => void
}

/**
 * Baixa o código de cada slug e grava em {baseFolder}/{slug}/...
 */
export async function runEdgeFunctionsBackup(
  options: RunEdgeFunctionsBackupOptions,
): Promise<EdgeFunctionsBackupProgress> {
  const {
    slugs,
    rootDir,
    baseFolder = 'edge-functions',
    signal,
    onProgress,
  } = options

  const failed: EdgeFunctionsBackupProgress['failed'] = []
  let done = 0
  const total = slugs.length

  const emit = (currentSlug: string | null) => {
    onProgress?.({ done, total, currentSlug, failed: [...failed] })
  }

  emit(null)

  for (const slug of slugs) {
    if (signal?.aborted) {
      throw new DOMException('Backup cancelado', 'AbortError')
    }

    emit(slug)

    try {
      const payload = await edgeFunctionsBackupService.getFiles(slug)
      for (const file of payload.files) {
        if (signal?.aborted) {
          throw new DOMException('Backup cancelado', 'AbortError')
        }
        const relativePath = `${baseFolder}/${slug}/${file.path}`
        await writeTextOrBinaryFile(rootDir, relativePath, file)
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err
      const message = err instanceof Error ? err.message : String(err)
      failed.push({ slug, error: message })
    }

    done += 1
    emit(slug)
  }

  const finalProgress: EdgeFunctionsBackupProgress = {
    done,
    total,
    currentSlug: null,
    failed,
  }
  onProgress?.(finalProgress)
  return finalProgress
}

export function formatEdgeFunctionUpdatedAt(
  value: number | string | null | undefined,
): string {
  if (value == null || value === '') return '—'
  const ms = typeof value === 'number' ? (value < 1e12 ? value * 1000 : value) : Date.parse(String(value))
  if (!Number.isFinite(ms)) return String(value)
  try {
    return new Date(ms).toLocaleString('pt-BR')
  } catch {
    return String(value)
  }
}
